// Parse free-form portion strings into a structured { qty, unit } shape so
// the UI can present "200 g" / "1.5 cups" as direct-edit fields instead of
// abstract x0.75 multipliers (which nobody thinks in).

/** Canonical unit names the UI knows how to render + step. */
export type CanonicalUnit =
  | "g"
  | "oz"
  | "ml"
  | "fl-oz"
  | "cup"
  | "tbsp"
  | "tsp"
  | "slice"
  | "piece"
  | "serving";

export interface ParsedPortion {
  /** Numeric quantity (e.g. 1.5 for "1.5 cups"). */
  qty: number;
  /** Canonical unit. */
  unit: CanonicalUnit;
  /** Original string we parsed (kept for display fallback). */
  raw: string;
  /** Pretty unit label suitable for inline display: "g", "cups", "slices". */
  label: string;
  /** Suggested step size for +/- buttons. */
  step: number;
}

/**
 * Try to parse a portion string like "1 cup", "150 g", "2 slices", "~3 tbsp"
 * into a {qty, unit} pair. Returns null when the string is ambiguous (no
 * number, or unit we don't recognize).
 */
// Unicode vulgar-fraction characters the LLMs (and copy-paste from the web)
// sometimes return. Mapped to their decimal value so parsePortion can read
// "½ cup", "1½ servings", "⅓ slice", etc. without us having to convert
// them at every call site.
const UNICODE_FRACTIONS: Record<string, number> = {
  "¼": 0.25,
  "½": 0.5,
  "¾": 0.75,
  "⅐": 1 / 7,
  "⅑": 1 / 9,
  "⅒": 0.1,
  "⅓": 1 / 3,
  "⅔": 2 / 3,
  "⅕": 0.2,
  "⅖": 0.4,
  "⅗": 0.6,
  "⅘": 0.8,
  "⅙": 1 / 6,
  "⅚": 5 / 6,
  "⅛": 0.125,
  "⅜": 0.375,
  "⅝": 0.625,
  "⅞": 0.875
};
const UNICODE_FRACTION_RE = new RegExp(`[${Object.keys(UNICODE_FRACTIONS).join("")}]`);

export function parsePortion(raw: string | undefined | null): ParsedPortion | null {
  if (!raw) return null;
  const cleaned = raw.trim();
  if (!cleaned) return null;

  let qtyText: string | null = null;
  let rest: string | null = null;

  // Unicode vulgar fraction — alone ("½ cup") or with a leading integer
  // ("1½ servings"). Handled before the ASCII fraction so "1/2" doesn't
  // shadow "½".
  const uni = cleaned.match(
    new RegExp(`^\\s*~?\\s*(\\d*)\\s*(${Object.keys(UNICODE_FRACTIONS).map((c) => "\\" + c).join("|")})\\s+(.+)$`)
  );
  if (uni) {
    const intPart = uni[1] ? parseFloat(uni[1]) : 0;
    const fracPart = UNICODE_FRACTIONS[uni[2]] ?? 0;
    qtyText = String(intPart + fracPart);
    rest = uni[3];
  }

  if (!qtyText) {
    // Handle simple ASCII fractions like "1/2 cup" (LLMs occasionally return them).
    const fraction = cleaned.match(/^\s*~?\s*(\d+)\s*\/\s*(\d+)\s+(.+)$/);
    if (fraction) {
      const num = parseFloat(fraction[1]);
      const den = parseFloat(fraction[2]);
      if (den > 0) {
        qtyText = String(num / den);
        rest = fraction[3];
      }
    }
  }

  if (!qtyText) {
    // "1.5 cups", "150 g", "~2 tbsp", "200g" (no space)
    const m = cleaned.match(/^\s*~?\s*(\d+(?:\.\d+)?)\s*([a-zA-Z][a-zA-Z\s.]*)\s*$/);
    if (!m) return null;
    qtyText = m[1];
    rest = m[2];
  }

  // Suppress the unused-var warning if no unicode match path runs.
  void UNICODE_FRACTION_RE;

  const qty = parseFloat(qtyText);
  if (!Number.isFinite(qty) || qty <= 0) return null;

  const unit = normalizeUnit(rest ?? "");
  if (!unit) return null;

  return {
    qty,
    unit,
    raw: cleaned,
    label: labelFor(unit, qty),
    step: stepFor(unit)
  };
}

const UNIT_MAP: Array<{ pattern: RegExp; canonical: CanonicalUnit }> = [
  { pattern: /^(g|grams?)$/i, canonical: "g" },
  { pattern: /^(oz|ounces?)$/i, canonical: "oz" },
  { pattern: /^(ml|milliliters?|millilitres?)$/i, canonical: "ml" },
  { pattern: /^(fl\.?\s*oz\.?|fluid\s*ounces?)$/i, canonical: "fl-oz" },
  { pattern: /^(cups?)$/i, canonical: "cup" },
  { pattern: /^(tbsp|tbs|tablespoons?)$/i, canonical: "tbsp" },
  { pattern: /^(tsp|teaspoons?)$/i, canonical: "tsp" },
  { pattern: /^(slices?)$/i, canonical: "slice" },
  { pattern: /^(pieces?|items?)$/i, canonical: "piece" },
  { pattern: /^(servings?|portions?)$/i, canonical: "serving" }
];

function normalizeUnit(raw: string): CanonicalUnit | null {
  const trimmed = raw.replace(/[.,]/g, "").trim().toLowerCase();
  for (const { pattern, canonical } of UNIT_MAP) {
    if (pattern.test(trimmed)) return canonical;
  }
  return null;
}

function labelFor(unit: CanonicalUnit, qty: number): string {
  const plural = qty !== 1;
  switch (unit) {
    case "g":
      return "g";
    case "oz":
      return "oz";
    case "ml":
      return "ml";
    case "fl-oz":
      return "fl oz";
    case "cup":
      return plural ? "cups" : "cup";
    case "tbsp":
      return "tbsp";
    case "tsp":
      return "tsp";
    case "slice":
      return plural ? "slices" : "slice";
    case "piece":
      return plural ? "pieces" : "piece";
    case "serving":
      return plural ? "servings" : "serving";
  }
}

function stepFor(unit: CanonicalUnit): number {
  switch (unit) {
    case "g":
    case "ml":
      return 5; // 5g / 5ml steps feel right for food
    case "oz":
    case "fl-oz":
      return 0.25;
    case "cup":
    case "tbsp":
    case "tsp":
      return 0.25;
    case "slice":
    case "piece":
    case "serving":
      return 1;
  }
}

/** Format a {qty, unit} back into a portion string for storage. */
export function formatPortion(qty: number, unit: CanonicalUnit): string {
  const rounded = qty < 10 ? Math.round(qty * 100) / 100 : Math.round(qty);
  return `${rounded} ${labelFor(unit, rounded)}`;
}

// ============================================================================
// Unit conversion — used when the user switches units on the wheel picker.
// Keeps the food amount constant (and therefore the macros) when converting
// within a category (volume↔volume, mass↔mass).
// Cross-category switches (e.g. cup → slice) return null — those need a
// density value we don't have.
// ============================================================================

/**
 * Each unit's value in a canonical category base unit (ml for volume, g for mass).
 * Using exact USDA values so the integer ratios (cup→tbsp = 16, cup→tsp = 48,
 * tbsp→tsp = 3, fl-oz→tbsp = 2) round cleanly.
 */
const VOLUME_TO_ML: Partial<Record<CanonicalUnit, number>> = {
  ml: 1,
  tsp: 4.92892, // 1 US tsp
  tbsp: 14.7868, // 1 US tbsp = 3 tsp
  "fl-oz": 29.5735, // 1 US fl oz = 2 tbsp
  cup: 236.588 // 1 US legal cup = 16 tbsp = 48 tsp = 8 fl oz
};
const MASS_TO_G: Partial<Record<CanonicalUnit, number>> = {
  g: 1,
  oz: 28.3495
};

/**
 * Returns the multiplier to apply when converting a quantity from `fromUnit`
 * to `toUnit`. For example, converting from cup to tbsp returns 16 (because
 * 1 cup = 16 tbsp). Returns null if the units are different categories
 * (e.g. cup → g), can't be converted without a density, or are count-based.
 */
export function unitConversionFactor(
  fromUnit: CanonicalUnit,
  toUnit: CanonicalUnit
): number | null {
  if (fromUnit === toUnit) return 1;
  if (fromUnit in VOLUME_TO_ML && toUnit in VOLUME_TO_ML) {
    return VOLUME_TO_ML[fromUnit]! / VOLUME_TO_ML[toUnit]!;
  }
  if (fromUnit in MASS_TO_G && toUnit in MASS_TO_G) {
    return MASS_TO_G[fromUnit]! / MASS_TO_G[toUnit]!;
  }
  return null;
}

// ============================================================================
// Cross-category conversion via grams.
//
// When we know the gram weight of "1 portion" of a food (grams_per_portion
// from the AI estimate or the generic-foods catalog) AND we know an approx
// density for the food's category, we can route ANY unit conversion through
// grams as a canonical middle:
//
//   any_unit_amount  ──toGrams──►  grams  ──fromGrams──►  any_other_unit
//
// This unlocks UX like "switch a yogurt from `serving` to `g`" or
// "switch pasta from `cup` to `oz`" — the macros stay accurate because
// the food amount in grams is preserved across the swap.
// ============================================================================

/** Coarse food categories we have a density estimate for. The AI returns
 *  one of these when it can identify the food's broad type; we fall back
 *  to a 1.0 g/ml default when unknown. Density values are g/ml.
 *
 *  Sources: USDA FoodData Central density tables + common cookbook refs.
 *  These are intentionally a small, curated list — adding more categories
 *  with weak data hurts accuracy more than it helps. */
export type FoodCategory =
  | "water_like"          // water, broth, plain milk-substitute beverages
  | "dairy_liquid"        // milk, cream, kefir
  | "dairy_thick"         // yogurt, cottage cheese, sour cream
  | "oil"                 // olive, vegetable, canola, butter (liquid)
  | "honey_syrup"         // honey, maple syrup, molasses
  | "juice_soda"          // fruit juice, soda, sweet drinks
  | "grain_cooked"        // cooked rice, quinoa, oats
  | "pasta_cooked"        // cooked pasta, noodles
  | "flour_powder"        // flour, cocoa, protein powder
  | "sugar_granular"      // white sugar, brown sugar, salt
  | "nuts_seeds"          // whole nuts, seeds (not nut butters)
  | "nut_butter"          // peanut butter, almond butter, tahini
  | "leafy_raw"           // raw lettuce, spinach, herbs (very low density)
  | "vegetable_chopped"   // chopped onion, pepper, raw vegetables
  | "fruit_chopped"       // diced fruit, berries
  | "beans_cooked"        // cooked beans, lentils, chickpeas
  | "meat_cooked"         // cooked chicken, beef, fish (~1.05)
  | "cereal_dry"          // dry cereal, granola
  | "other";              // unknown → 1.0 default

const DENSITY_G_PER_ML: Record<FoodCategory, number> = {
  water_like: 1.0,
  dairy_liquid: 1.03,
  dairy_thick: 1.04,
  oil: 0.92,
  honey_syrup: 1.4,
  juice_soda: 1.04,
  grain_cooked: 0.74,
  pasta_cooked: 0.62,
  flour_powder: 0.53,
  sugar_granular: 0.85,
  nuts_seeds: 0.55,
  nut_butter: 1.08,
  leafy_raw: 0.05,
  vegetable_chopped: 0.6,
  fruit_chopped: 0.65,
  beans_cooked: 0.7,
  meat_cooked: 1.05,
  cereal_dry: 0.4,
  other: 1.0
};

export function densityFor(category: FoodCategory | null | undefined): number {
  if (!category) return 1.0;
  return DENSITY_G_PER_ML[category] ?? 1.0;
}

/** Heuristic category guess from a food name. Used as a fallback when the
 *  AI doesn't return an explicit category. Keep this list short and high-
 *  precision — better to guess "other" than to mis-categorize.
 *
 *  Tip: if the user logs something the heuristic guesses wrong, the AI
 *  prompt update is the correct long-term fix, not adding more regexes. */
export function guessFoodCategory(name: string | undefined | null): FoodCategory {
  if (!name) return "other";
  const n = name.toLowerCase();
  // Order matters — more specific matches first.
  if (/butter|peanut butter|almond butter|tahini|nutella/.test(n) && /butter|nutella|tahini/.test(n)) return "nut_butter";
  if (/yogurt|cottage|sour cream|fage|greek/.test(n)) return "dairy_thick";
  if (/\bmilk\b|cream|kefir|half-and-half/.test(n)) return "dairy_liquid";
  if (/oil|olive oil|canola/.test(n)) return "oil";
  if (/honey|syrup|maple|molasses|agave/.test(n)) return "honey_syrup";
  if (/juice|soda|cola|sprite|gatorade/.test(n)) return "juice_soda";
  if (/rice|quinoa|barley|oatmeal|oats(?!.*dry)/.test(n)) return "grain_cooked";
  if (/pasta|spaghetti|noodles|macaroni|penne|fusilli|ramen/.test(n)) return "pasta_cooked";
  if (/flour|cocoa|protein powder/.test(n)) return "flour_powder";
  if (/\bsugar\b|salt|stevia/.test(n)) return "sugar_granular";
  if (/cereal|granola|muesli/.test(n)) return "cereal_dry";
  if (/almond|walnut|pecan|cashew|pistachio|peanut|seed/.test(n) && !/butter/.test(n)) return "nuts_seeds";
  if (/lettuce|spinach|kale|arugula|herb|cilantro|parsley|basil/.test(n)) return "leafy_raw";
  if (/onion|pepper|carrot|celery|cucumber|tomato|broccoli|cauliflower|zucchini/.test(n)) return "vegetable_chopped";
  if (/berr(y|ies)|grape|apple|peach|cherry|melon|mango|pineapple/.test(n)) return "fruit_chopped";
  if (/bean|lentil|chickpea|garbanzo/.test(n)) return "beans_cooked";
  if (/chicken|beef|pork|turkey|fish|salmon|tuna|shrimp|steak|sausage|bacon/.test(n)) return "meat_cooked";
  return "other";
}

/** Try to extract a gram weight from a portion string's parenthetical hint.
 *  Examples that match: "1 cup (~158g)", "1 medium (~123g)", "4 oz (113g)".
 *  Returns null if no gram hint is present. Used to back-fill grams_per_portion
 *  for legacy items that pre-date the AI returning it as a structured field. */
export function parseGramsFromPortion(portion: string | undefined | null): number | null {
  if (!portion) return null;
  const m = portion.match(/\(\s*~?\s*(\d+(?:\.\d+)?)\s*g\s*\)/i);
  if (!m) return null;
  const g = parseFloat(m[1]);
  return Number.isFinite(g) && g > 0 ? g : null;
}

/** Convert a (qty, unit) pair to grams, given the food's metadata.
 *
 *  - mass units use exact USDA ratios (g, oz)
 *  - volume units require density (g/ml) — pass density for the food's category
 *  - count units (serving/piece/slice) require gramsPerPortion (gram weight of
 *    ONE unit as defined by the original portion)
 *
 *  Returns null if conversion is impossible (e.g. count → grams with no
 *  gramsPerPortion provided). Callers should handle null gracefully —
 *  typically by skipping the cross-category option in the UI. */
export function toGrams(
  qty: number,
  unit: CanonicalUnit,
  options: { density?: number; gramsPerPortion?: number } = {}
): number | null {
  if (!Number.isFinite(qty) || qty <= 0) return null;
  if (unit === "g") return qty;
  if (unit === "oz") return qty * MASS_TO_G.oz!;
  // Volume: qty × ml_per_unit × density
  if (unit in VOLUME_TO_ML) {
    const density = options.density ?? 1.0;
    return qty * VOLUME_TO_ML[unit]! * density;
  }
  // Count-based: requires gramsPerPortion (weight of ONE unit)
  if (unit === "serving" || unit === "piece" || unit === "slice") {
    if (options.gramsPerPortion == null) return null;
    return qty * options.gramsPerPortion;
  }
  return null;
}

/** Convert grams → (qty, unit) for the target unit. Inverse of toGrams.
 *  Returns null when conversion is impossible. */
export function fromGrams(
  grams: number,
  targetUnit: CanonicalUnit,
  options: { density?: number; gramsPerPortion?: number } = {}
): number | null {
  if (!Number.isFinite(grams) || grams <= 0) return null;
  if (targetUnit === "g") return grams;
  if (targetUnit === "oz") return grams / MASS_TO_G.oz!;
  if (targetUnit in VOLUME_TO_ML) {
    const density = options.density ?? 1.0;
    // grams → ml via density, then ml → target volume unit
    const ml = grams / density;
    return ml / VOLUME_TO_ML[targetUnit]!;
  }
  if (targetUnit === "serving" || targetUnit === "piece" || targetUnit === "slice") {
    if (options.gramsPerPortion == null) return null;
    return grams / options.gramsPerPortion;
  }
  return null;
}

/**
 * Scale a portion label by a quantity, preserving as much of the original
 * formatting as possible. Used both by the quick-log flow (scaleEstimate)
 * and the MealReview confirm() bake-in so the saved portion text never
 * lies about what was logged.
 *
 * Strategy, in order:
 *   1. quantity == 1  → return as-is
 *   2. parsePortion succeeds → use formatPortion ("1 cup" → "2 cups")
 *   3. Leading-number pattern matches → multiply the leading number AND
 *      any (~Ng) parenthetical hint ("1 large (~50g)" → "2 large (~100g)")
 *   4. Last resort → append " × N" so the count is at least visible
 *
 * Why not always × N? Because "1 cup × 2" reads worse than "2 cups", and
 * "1 large (~50g) × 2" reads worse than "2 large (~100g)". We bias toward
 * natural English when we can.
 */
export function scalePortionText(portion: string | undefined | null, quantity: number): string {
  if (!portion) return portion ?? "";
  if (!Number.isFinite(quantity) || quantity === 1) return portion;

  // Path 1: structured "N unit" string → re-emit via formatPortion.
  const parsed = parsePortion(portion);
  if (parsed) {
    return formatPortion(parsed.qty * quantity, parsed.unit);
  }

  // Path 2: leading number ("1 medium (~123g)", "1 large (~50g)",
  // "1 slice (~28g)", "4 oz cooked (~113g)"). Capture and multiply the
  // leading int/decimal/fraction, then ALSO scale any (~Ng) gram hint
  // so the displayed weight stays roughly accurate.
  const leading = portion.match(/^\s*(\d+(?:\.\d+)?(?:\/\d+)?)\s+(.+)$/);
  if (leading) {
    const rawQty = leading[1];
    const rest = leading[2];
    let qtyValue: number;
    if (rawQty.includes("/")) {
      const [num, den] = rawQty.split("/").map(Number);
      qtyValue = den > 0 ? num / den : NaN;
    } else {
      qtyValue = parseFloat(rawQty);
    }
    if (Number.isFinite(qtyValue)) {
      const newQty = qtyValue * quantity;
      // Pretty-print: integers stay int, fractions get 2 decimals max.
      const newQtyStr = newQty % 1 === 0 ? String(newQty) : String(Math.round(newQty * 100) / 100);
      // Scale any (~Ng) / (~Nml) parenthetical so the gram hint stays roughly accurate
      const scaledRest = rest.replace(
        /\(\s*~?\s*(\d+(?:\.\d+)?)\s*(g|ml)\s*\)/i,
        (_match, n, unit) => {
          const scaled = Math.round(parseFloat(n) * quantity);
          return `(~${scaled}${unit.toLowerCase()})`;
        }
      );
      return `${newQtyStr} ${scaledRest}`;
    }
  }

  // Path 3: already has a "× N" suffix → don't double-stamp.
  if (/×\s*[\d.]+\s*$/.test(portion)) return portion;

  // Path 4: unparseable. Append a count so the user at least sees it.
  return `${portion} × ${quantity}`;
}
